import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Unicode fractions mapping
const UNICODE_FRACTIONS = {
  '½': 0.5, '⅓': 1/3, '⅔': 2/3, '¼': 0.25, '¾': 0.75,
  '⅕': 0.2, '⅖': 0.4, '⅗': 0.6, '⅘': 0.8,
  '⅙': 1/6, '⅚': 5/6, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875
};

/**
 * Parse a fraction string like "1/2", "1 1/2", "½", or "1 ½" into a number
 * @param {string|number} str - The string to parse
 * @returns {number} - The parsed number, or NaN if not parseable
 */
export function parseFraction(str) {
  if (str === null || str === undefined) return NaN;
  if (typeof str === 'number') return str;
  
  const trimmed = String(str).trim();
  if (!trimmed) return NaN;
  
  // Check for unicode fractions (e.g., "½", "1 ½")
  for (const [frac, val] of Object.entries(UNICODE_FRACTIONS)) {
    if (trimmed.includes(frac)) {
      const parts = trimmed.split(frac);
      const whole = parseFloat(parts[0]) || 0;
      return whole + val;
    }
  }
  
  // Handle "X/Y" format (e.g., "1/2", "3/4")
  if (trimmed.includes('/')) {
    // Check for mixed number like "1 1/2"
    const mixedMatch = trimmed.match(/^(\d+)\s+(\d+)\/(\d+)$/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const num = parseInt(mixedMatch[2]);
      const denom = parseInt(mixedMatch[3]);
      if (denom !== 0) return whole + (num / denom);
    }
    
    // Simple fraction like "1/2"
    const fracMatch = trimmed.match(/^(\d+)\/(\d+)$/);
    if (fracMatch) {
      const num = parseInt(fracMatch[1]);
      const denom = parseInt(fracMatch[2]);
      if (denom !== 0) return num / denom;
    }
  }
  
  // Regular number (including decimals like "0.5")
  return parseFloat(trimmed);
}

/**
 * Convert a decimal number to a nice fraction string
 * @param {number} num - The number to convert
 * @returns {string} - A formatted string with unicode fractions when possible
 */
export function numberToFraction(num) {
  if (isNaN(num)) return '';
  
  // Handle whole numbers
  if (num === Math.floor(num)) return num.toString();
  
  const whole = Math.floor(num);
  const frac = num - whole;
  
  // Map common fractions
  const fractionMap = [
    { val: 0.125, str: '⅛' },
    { val: 0.25, str: '¼' },
    { val: 1/3, str: '⅓' },
    { val: 0.375, str: '⅜' },
    { val: 0.5, str: '½' },
    { val: 0.625, str: '⅝' },
    { val: 2/3, str: '⅔' },
    { val: 0.75, str: '¾' },
    { val: 0.875, str: '⅞' },
  ];
  
  // Find closest fraction match
  for (const { val, str } of fractionMap) {
    if (Math.abs(frac - val) < 0.02) {
      return whole > 0 ? `${whole} ${str}` : str;
    }
  }
  
  // No fraction match - format as decimal
  return num.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Scale a quantity string by a ratio and return a formatted string
 * @param {string|number} quantity - The original quantity (can be fraction like "1/2")
 * @param {number} ratio - The scaling ratio (e.g., 2 for doubling)
 * @returns {string} - The scaled quantity as a formatted string
 */
export function scaleQuantity(quantity, ratio = 1) {
  const num = parseFraction(quantity);
  if (isNaN(num)) return quantity; // Return original if not a number
  const scaled = num * ratio;
  return numberToFraction(scaled);
}
